import React, { useRef } from 'react';
import { AccessibilityInfo, InteractionManager, Platform, findNodeHandle, } from 'react-native';
import { useRoute } from '@react-navigation/native';
import TENANT_ID from '../config/TenantId';
import { Metric } from '../metrics';
import { toErrorMessage } from '../util';
import { getShowtimeContext } from './ShowtimeContext';
export function useConfig() {
    const { config } = React.useContext(getShowtimeContext());
    return config;
}
export function useAppContext() {
    const { appContext } = React.useContext(getShowtimeContext());
    return appContext;
}
export function useUserContext() {
    const { userContext } = React.useContext(getShowtimeContext());
    return userContext;
}
export function useActions() {
    const { actions } = React.useContext(getShowtimeContext());
    return actions;
}
export function useAtoZUrls() {
    const { providableUrls } = React.useContext(getShowtimeContext());
    const { atozBaseUrl, atozApiGatewayUrl } = useConfig();
    const { enabledFeatures } = useUserContext();
    const createAtoZPath = (path) => `${atozBaseUrl}${path}`;
    const createAtoZGatewayPath = (path) => `${atozApiGatewayUrl}${path}`;
    const extendUrl = (url, params) => `${url}${params}`;
    const isMobilePhonetool = () => {
        if (Platform.OS === 'web') {
            return false;
        }
        const params = useRoute().params;
        return params ? params.initialUri.startsWith(`${atozBaseUrl}/phonetool`) : false;
    };
    const createBadgePhotoUrl = (employeeId) => {
        let badgePhotoUrl;
        if (enabledFeatures.includes('phone_tool_badge_photo_url') && isMobilePhonetool()) {
            const phonetoolBadgePhotoPath = '/badge_photo_phone_tool';
            badgePhotoUrl = createAtoZPath(phonetoolBadgePhotoPath);
        }
        else if (enabledFeatures.includes('mobile_badge_photo_url')) {
            const mobileEndpointProd = 'https://atoz-badge-photo.amazon.work';
            const mobileEndpointBeta = 'https://atoz-badge-photo.integ.amazon.com';
            badgePhotoUrl = __DEV__ ? mobileEndpointBeta : mobileEndpointProd;
        }
        else {
            const badgePhotoPath = '/badge_photo';
            badgePhotoUrl = createAtoZPath(badgePhotoPath);
        }
        if (employeeId) {
            const todaysDate = (new Date()).toISOString().slice(0, 10);
            return extendUrl(badgePhotoUrl, `/${employeeId}?date=${todaysDate}`);
        }
        return badgePhotoUrl;
    };
    const shiftsSchedulePage = createAtoZPath('/shifts/schedule');
    const newsPage = createAtoZPath('/news');
    const clockPunchPage = createAtoZPath('/submitPunch');
    const findShiftsPage = createAtoZPath('/shifts/schedule/find');
    const rtoPage = createAtoZPath('/time/request');
    const vtoPage = createAtoZPath('/time/optional');
    const ssrPage = createAtoZPath('/resignation');
    const celebrationsPage = createAtoZPath('/celebrations');
    const shoutOutsPage = createAtoZPath('/shout-outs');
    const myHRPage = createAtoZPath('/myhr');
    const myHRFabPage = createAtoZPath('/myhr-fab');
    const myPayPage = createAtoZPath('/pay');
    const logout = createAtoZPath('/logout');
    const phonetoolPage = createAtoZPath('/phonetool');
    const shiftCountUrl = createAtoZGatewayPath('/shiftCount');
    const thisWeekTile = createAtoZGatewayPath('/scheduledDuration');
    return {
        createAtoZPath,
        createAtoZGatewayPath,
        createBadgePhotoUrl,
        extendUrl,
        shiftsSchedulePage,
        newsPage,
        clockPunchPage,
        findShiftsPage,
        rtoPage,
        vtoPage,
        ssrPage,
        celebrationsPage,
        shoutOutsPage,
        shiftCountUrl,
        thisWeekTile,
        myHRPage,
        myHRFabPage,
        myPayPage,
        logout,
        phonetoolPage,
        shiftTransferUrl: providableUrls === null || providableUrls === void 0 ? void 0 : providableUrls.shiftTransferUrl,
    };
}
export const useAccessibilityFocus = () => {
    const ref = useRef(null);
    const setFocus = () => {
        InteractionManager.runAfterInteractions(() => {
            if (ref && ref.current) {
                const focusPoint = findNodeHandle(ref.current);
                if (focusPoint) {
                    try {
                        AccessibilityInfo.setAccessibilityFocus(focusPoint);
                    }
                    catch (error) {
                        Metric('UseAccessibilityFocusError', TENANT_ID, {
                            attributes: {
                                error: toErrorMessage(error),
                            },
                        }).put();
                    }
                }
            }
        });
    };
    return [ref, setFocus];
};
//# sourceMappingURL=hooks.js.map